import { forwardRef, type ReactNode, type Ref } from "react";

import { Row, type RowProps } from "@hightouch/htui";

import { DefaultPageContainerPadding } from "src/components/layout/page-container";
import { SIZES } from "src/ui/box";
import { useNavWidth } from "src/contexts/nav-width-context";

interface Props {
  fit?: boolean;
  children: ReactNode;
  ref?: Ref<HTMLDivElement>;
  center?: boolean;
}

// TODO(find these and see if they're necessary)
export const ActionBarHeight = 72;

const styles: RowProps = {
  borderTopWidth: "hairline",
  borderTopStyle: "solid",
  borderColor: "base.border",
  bg: "white",
  minHeight: `${ActionBarHeight}px`,
  width: "100%",
  align: "center",
};

/**
 * @deprecated Use FormBar instead
 */
export const ActionBar = forwardRef<HTMLDivElement, Props>(
  ({ children, fit, center = true }, ref) => {
    const { navWidth } = useNavWidth();

    if (fit) {
      return (
        <Row
          ref={ref}
          {...styles}
          pos="sticky"
          bottom={0}
          px={DefaultPageContainerPadding.X}
          justifyContent="space-between"
        >
          {children}
        </Row>
      );
    }
    return (
      <Row
        ref={ref}
        {...styles}
        justify={center ? "center" : "flex-start"}
        position="fixed"
        bottom={0}
        right={0}
        left={navWidth}
        maxWidth={{
          base: `calc(100% - ${navWidth.base})`,
          lg: `calc(100% - ${navWidth.lg})`,
        }}
        zIndex={1}
      >
        <Row
          justifyContent="space-between"
          maxWidth={center ? SIZES.page : "unset"}
          width="100%"
          pl={DefaultPageContainerPadding.X}
          pr={16}
        >
          {children}
        </Row>
      </Row>
    );
  },
);

ActionBar.displayName = "ActionBar";

const floatingActionBarStyles: RowProps = {
  borderRadius: "2xl",
  boxShadow: "md",
  bg: "white",
  minHeight: `${ActionBarHeight}px`,
  width: "100%",
  align: "center",
};

/**
 * @deprecated Use FormBar instead
 */
export const FloatingActionBar = forwardRef<HTMLDivElement, Props>(
  ({ children, fit, center = true }, ref) => {
    const { navWidth } = useNavWidth();

    if (fit) {
      return (
        <Row
          ref={ref}
          {...floatingActionBarStyles}
          width="auto"
          pos="sticky"
          bottom={4}
          mx={2}
          px={DefaultPageContainerPadding.X}
          justifyContent="space-between"
        >
          {children}
        </Row>
      );
    }
    return (
      <Row
        ref={ref}
        {...floatingActionBarStyles}
        position="fixed"
        bottom={4}
        px={4}
        left={{
          base: `calc(${navWidth.base} + 20px)`,
          lg: `calc(${navWidth.lg} + 20px)`,
        }}
        maxWidth={{
          base: `calc(100% - ${navWidth.base} - 40px)`,
          lg: `calc(100% - ${navWidth.lg} - 40px)`,
        }}
        zIndex={1}
      >
        <Row
          justifyContent="space-between"
          maxWidth={center ? SIZES.page : "unset"}
          width="100%"
        >
          {children}
        </Row>
      </Row>
    );
  },
);

FloatingActionBar.displayName = "FloatingActionBar";
